import React from 'react';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import { VMConfig } from '../../../types/vm.type';
import { PricingAddon, PricingPlan } from '../../../services/pricing.service';

interface Step2VMPricingProps {
  config: VMConfig;
  setConfig: (config: VMConfig | ((prev: VMConfig) => VMConfig)) => void;
  plans: PricingPlan[];
  selectedPlanCode: string | null;
  onSelectPlan: (planCode: string) => void;
  planVariants: PricingPlan[];
  selectedVariantCode: string | null;
  onSelectVariant: (variantCode: string) => void;
  selectedPlan: PricingPlan | null;
  addons: PricingAddon[];
  isLoadingPlans: boolean;
  isLoadingAddons: boolean;
}

const formatCurrency = (amount?: number, currency?: string): string => {
  if (amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
  }).format(amount);
};

const Step2VMPricing: React.FC<Step2VMPricingProps> = ({
  config,
  setConfig,
  plans,
  selectedPlanCode,
  onSelectPlan,
  planVariants,
  selectedVariantCode,
  onSelectVariant,
  selectedPlan,
  addons,
  isLoadingPlans,
  isLoadingAddons,
}) => {
  const { t } = useTranslation();
  const backupAddon = addons.find((addon) => addon.addonCode === 'BACKUP_DAILY');
  const baseAmount = selectedPlan?.priceAmount ?? 0;
  const selectedAddonsAmount = addons.reduce((sum, addon) => {
    if (addon.addonCode === 'BACKUP_DAILY' && config.backups) {
      return sum + (addon.priceAmount || 0);
    }
    return sum;
  }, 0);
  const totalAmount = baseAmount + selectedAddonsAmount;

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('virtualMachines.create.pricing.billingPlanLabel')}
        </label>
        {isLoadingPlans ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
        ) : plans.length === 0 ? (
          <p className="text-sm text-red-600 dark:text-red-400">
            {t('virtualMachines.create.pricing.noPlansForOs')}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {plans.map((plan) => (
              <button
                key={plan.planCode}
                onClick={() => onSelectPlan(plan.planCode)}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  selectedPlanCode === plan.planCode
                    ? 'border-black dark:border-white bg-gray-50 dark:bg-gray-700'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <p className="font-semibold text-gray-900 dark:text-white">{plan.planName}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{plan.planCode}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                  {(plan.providerConfiguration?.instanceType || '-')} • {plan.includedResources?.vcpu || '-'} vCPU •{' '}
                  {plan.includedResources?.memoryMb || '-'} MB RAM • {plan.includedResources?.storageGb || '-'} GB
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {t('virtualMachines.create.pricing.basePricePrefix')} {formatCurrency(plan.priceAmount, plan.currency)}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('virtualMachines.create.pricing.variantsLabel')}
        </label>
        {!selectedPlanCode ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('virtualMachines.create.pricing.selectPlanForVariants')}
          </p>
        ) : planVariants.length <= 1 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('virtualMachines.create.pricing.noAdditionalVariants')}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {planVariants.map((variant) => {
              const variantKey = variant.variantCode || 'STANDARD';
              return (
                <button
                  key={`${variant.planCode}-${variantKey}`}
                  onClick={() => onSelectVariant(variantKey)}
                  className={`px-3 py-2 rounded-lg border-2 text-left transition-all ${
                    selectedVariantCode === variantKey
                      ? 'border-black dark:border-white bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                      : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <p className="text-sm font-semibold">
                    {variant.variantName || variant.displayName || variant.variantCode || 'STANDARD'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatCurrency(variant.priceAmount, variant.currency)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {variant.providerConfiguration?.instanceType || '-'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {variant.includedResources?.vcpu ?? '-'} vCPU • {variant.includedResources?.memoryMb ?? '-'} MB RAM
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {variant.includedResources?.storageGb ?? '-'} GB {t('virtualMachines.create.pricing.storageShort')} • {variant.includedResources?.transferGb ?? '-'} GB {t('virtualMachines.create.pricing.transferShort')}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div>
          <p className="font-medium text-gray-900 dark:text-white mb-2">{t('virtualMachines.create.pricing.addonsTitle')}</p>
          {isLoadingAddons ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          ) : addons.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('virtualMachines.create.pricing.noAddonsAvailable')}
            </p>
          ) : (
            <div className="space-y-2">
              {addons.map((addon) => (
                <label key={addon.priceId || addon.addonCode} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addon.addonCode === 'BACKUP_DAILY' ? config.backups : false}
                    onChange={(e) => {
                      if (addon.addonCode === 'BACKUP_DAILY') {
                        setConfig((prev) => ({ ...prev, backups: e.target.checked }));
                      }
                    }}
                    disabled={addon.addonCode !== 'BACKUP_DAILY'}
                    className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-black dark:text-white focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-700"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{addon.addonName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {addon.description || t('virtualMachines.create.pricing.addonNoDescription')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatCurrency(addon.priceAmount, addon.currency)} / {t('virtualMachines.create.pricing.month')}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}
          {backupAddon && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('virtualMachines.create.pricing.backupsHint')}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('virtualMachines.create.pricing.selectedPlanPriceLabel')}
        </label>
        {!selectedPlanCode ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('virtualMachines.create.pricing.selectPlanForUpdatedPrice')}
          </p>
        ) : (
          <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/40">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-gray-900 dark:text-white">
                {selectedPlan?.planName || selectedPlanCode}
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(
                  totalAmount,
                  selectedPlan?.currency
                )}
              </p>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {selectedPlan?.providerConfiguration?.instanceType || config.instanceType} • {selectedPlan?.currency || 'MXN'} / {t('virtualMachines.create.pricing.month')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('virtualMachines.create.step1.storage')}: {selectedPlan?.includedResources?.storageGb ?? config.storage} GB
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
              {t('virtualMachines.create.pricing.includedResourcesPrefix')} {selectedPlan?.includedResources?.vcpu ?? '-'} vCPU •{' '}
              {selectedPlan?.includedResources?.memoryMb ?? '-'} MB RAM •{' '}
              {selectedPlan?.includedResources?.storageGb ?? '-'} GB
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('virtualMachines.create.pricing.totalIncludesAddons')}
            </p>
          </div>
        )}
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
        <div className="flex items-start gap-2">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 text-lg mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
              {t('virtualMachines.create.pricing.dynamicPlansTitle')}
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {t('virtualMachines.create.pricing.dynamicPlansDescription')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2VMPricing;
